import React from 'react';
import PropTypes from 'prop-types';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../../../../../context/I18nContext';
import { useModal } from '../../../../../context/ModalContext';
import { useToast } from '../../../../../context/ToastContext';
import { useRoomContext } from '../../../../../context/RoomContext';
import { useMatrixClient } from '../../../../../context/MatrixContext';
import { useForwarding } from '../../../../../context/ForwardingContext';
import { useDisambiguation } from '../../../../../context/DisambiguationContext';
import { usePinnedMessages, isEventPinnedInCache } from '../../../../../hooks/usePinnedMessages';
import FancyTimeStamp from '../../../../FancyTimeStamp';
import MessageStatusIndicator from './MessageStatusIndicator';
import SmartEmojiPicker from './SmartEmojiPicker';
import MessageReactions from './MessageReactions';
import { useMediaThumbnail } from '../../../../../hooks/useMediaThumbnail';
import { sanitizeFilename, formatFileSize, getFileType, getAttachmentIcon, getAttachmentUrl } from '../../../../../utils/fileUtils';
import logger from '../../../../../utils/logger';
import { getRedactionInfo, getRedactionTranslation } from '../../../../../utils/redactionUtils';
import { canSendMessages } from '../../../../../utils/roomUtils';
import { stripReplyFallback } from '../../../../../utils/replyUtils';
import MentionPill from '../../../../../components/MentionPill';
import { parseMentionsFromHtml, hasMentionForUser } from '../../../../../utils/mentionParsing';
import { linkify } from '../../../../../utils/linkifyUtils';
import AttachmentPreviewModal from './AttachmentPreviewModal';
import FileAttachmentModal from './FileAttachmentModal';
import ReportMessageModal from '../../../../Modal/ReportMessageModal';
import LocationMessage from './LocationMessage';
import './MessageBubble.css';
import { MESSAGE_TYPES, NON_EDITABLE_MESSAGE_TYPES, CONTEXT_MENU, EMOJI_PICKER_DIMENSIONS } from '../../../../../constants';
import { MAX_PINS_PER_ROOM } from '../../../../../constants/limits';

// EventShieldReason enum replacement
const EventShieldReason = {
  UNVERIFIED_IDENTITY: 'UNVERIFIED_IDENTITY',
  UNSIGNED_DEVICE: 'UNSIGNED_DEVICE',
  UNKNOWN_DEVICE: 'UNKNOWN_DEVICE',
  VERIFICATION_VIOLATION: 'VERIFICATION_VIOLATION',
};

// matrix-js-sdk DecryptionFailureCode values for a message from a non-cross-signed sender.
// DEFENSIVE FALLBACK: under the current A_25514 config (OnlySignedDevicesIsolationMode +
// the vendored inbound-decouple patch, patches/matrix-js-sdk+41.0.0.patch) INBOUND decryption
// is permissive, so these codes are NOT produced in normal operation. This branch only fires
// if that patch is ever lost (clean install / SDK bump) — in which case it surfaces the failure
// as a verification issue rather than a generic "Unable to decrypt".
const DECRYPTION_FAILURE_UNSIGNED_SENDER = 'UNSIGNED_SENDER_DEVICE';
const DECRYPTION_FAILURE_UNKNOWN_SENDER = 'UNKNOWN_SENDER_DEVICE';

const KebabMenuIcon = ({ onClick = () => {}, renderSide = 'left' }) => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 14 14'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    onClick={onClick}
    style={{
      transform: renderSide === 'right' ? 'scaleX(-1)' : 'none',
      paddingLeft: '10px',
    }}>
    <path
      d='M2 3.5C2.9625 3.5 3.75 2.7125 3.75 1.75C3.75 0.7875 2.9625 0 2 0C1.0375 0 0.25 0.7875 0.25 1.75C0.25 2.7125 1.0375 3.5 2 3.5ZM2 5.25C1.0375 5.25 0.25 6.0375 0.25 7C0.25 7.9625 1.0375 8.75 2 8.75C2.9625 8.75 3.75 7.9625 3.75 7C3.75 6.0375 2.9625 5.25 2 5.25ZM2 10.5C1.0375 10.5 0.25 11.2875 0.25 12.25C0.25 13.2125 1.0375 14 2 14C2.9625 14 3.75 13.2125 3.75 12.25C3.75 11.2875 2.9625 10.5 2 10.5Z'
      fill='#4A4A4A'
    />
  </svg>
);

KebabMenuIcon.propTypes = {
  onClick: PropTypes.func,
  renderSide: PropTypes.oneOf(['left', 'right']),
};

const UnverifiedDeviceIcon = () => (
  <span
    title='Message sent from an unverified device'
    style={{ color: '#e67e22', marginLeft: 6, verticalAlign: 'middle' }}>
    <i className='fa fa-exclamation-triangle' />
  </span>
);

const MessageBubble = ({
  message,
  name = '',
  isUser = false,
  shield = null,
  isRead = false,
  isFirstInGroup = false,
  isLastInGroup = false,
  onDeleteMessage = (_messageId) => {},
  onReplyMessage = (_message, _senderName, _content) => {},
  onEditMessage = (_message, _content) => {},
  isEditHighlighted = false,
  isSelected = false,
  isReply = false,
  isEdited = false,
  originalReplyMessage = null,
  onReactionDetailsOpen = () => {},
  onMessageDetailsOpen = () => {},
  onFocusMessage = () => {},
  onEditHistoryOpen = () => {},
  onMentionClick = () => {},
  retryingTransactionIds = null,
  pendingTransactionIds = null,
  unsentTransactionIds = null,
}) => {
  const { t } = useI18n();
  const { showConfirm, showInfo, showError } = useModal();
  const { showError: showToastError } = useToast();
  const { room, toggleReaction, canDeleteMessage, powerLevelsCounter, members, isServerNotices, roomId, reportMessage, isEncrypted: isRoomEncrypted } = useRoomContext();
  const client = useMatrixClient();
  const { startForwarding } = useForwarding();
  const { needsDisambiguation } = useDisambiguation();

  // Check if user has permission to send messages (required for reactions, replies, forwarding)
  const hasMessagePermission = useMemo(
    () => canSendMessages(room, client),
    [room, client, powerLevelsCounter],
  );
  const { pinnedEventIds, canPin, pinEvent, unpinEvent } = usePinnedMessages(roomId);
  // Check both the React state and the shared cache for pinned status.
  // The cache check is critical for components that remount after scrolling,
  // as the React state may not be initialized yet on the first render.
  // Pass the client as fallback to read directly from room state if cache is empty.
  const messageId = message.getId?.();
  const inState = pinnedEventIds.includes(messageId);
  const inCache = isEventPinnedInCache(roomId, messageId, client);
  const isPinned = inState || inCache;

  // Messages have a local ID (e.g., ~!roomId:m12345.0) until they sync with the server,
  // at which point they get a server ID (e.g., $eventId123). Pinning a message before
  // sync would store the local ID, which becomes invalid after sync.
  // Only allow pinning messages that have synced (server IDs start with '$').
  const hasServerEventId = messageId?.startsWith('$');

  const [isHovered, setIsHovered] = useState(false);
  const [isHoveringReactions, setIsHoveringReactions] = useState(false);
  const [hasRevealedDetails, setHasRevealedDetails] = useState(isLastInGroup);
  const [isContextMenuOpen, setOpenContextMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMenuSticky, setIsMenuSticky] = useState(false);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [optimisticReaction, setOptimisticReaction] = useState(null); // { emoji, timestamp }
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [isOpeningDetails, setIsOpeningDetails] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [emojiPanelPositionStyle, setEmojiPanelPositionStyle] = useState({});
  const [initialButtonRect, setInitialButtonRect] = useState(null);
  const contextMenuRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const bubbleRef = useRef(null);
  const portalContainerRef = useRef(null);

  // Sync hasRevealedDetails with isLastInGroup prop changes
  // This ensures that when message groups are recalculated (e.g., after decryption),
  // the timestamp visibility is correctly reset based on the updated position
  useEffect(() => {
    setHasRevealedDetails(isLastInGroup);
  }, [isLastInGroup]);

  // Check delete permissions when message changes or when message sync status changes
  // This allows moderators to delete any message, not just their own
  // Including message.status ensures re-check when message syncs (status changes from pending to null)
  // Including powerLevelsCounter ensures re-check when room power levels change
  useEffect(() => {
    const checkDeletePermission = async () => {
      if (!message || !canDeleteMessage) {
        setCanDelete(false);
        return;
      }

      try {
        const hasPermission = await canDeleteMessage(message.getId());
        setCanDelete(hasPermission);
      } catch (error) {
        logger.error('Failed to check delete permission:', error);
        setCanDelete(false);
      }
    };

    checkDeletePermission();
  }, [message, message?.status, canDeleteMessage, powerLevelsCounter]);

  // Clear optimistic reaction once the real one appears in server reactions
  useEffect(() => {
    if (!optimisticReaction) return;
    
    const currentUserId = client?.getUserId();
    if (!currentUserId) return;
    
    // Check if the real reaction has appeared in message._reactions
    const reactors = message._reactions?.get(optimisticReaction.emoji);
    if (reactors) {
      const userReaction = reactors.find(r => r.senderId === currentUserId && r.eventId !== 'optimistic');
      if (userReaction) {
        // Real reaction has arrived, clear optimistic state
        setOptimisticReaction(null);
      }
    }
  }, [optimisticReaction, message._reactions, client]);

  // Merge server reactions with optimistic updates
  const displayReactions = (() => {
    // Don't show reactions on deleted messages
    if (message.isRedacted()) {
      return new Map();
    }

    const currentUserId = client?.getUserId();

    if (!optimisticReaction) {
      // Even without optimistic state, filter out current user's failed/pending reactions
      const filtered = new Map();
      message._reactions.forEach((reactors, emoji) => {
        const validReactors = reactors.filter((r) => {
          // Keep reactions from other users
          if (r.senderId !== currentUserId) return true;
          
          // For current user, check if the reaction event exists and is valid
          // Filter out reactions that failed to send or are still pending
          const room = client?.getRoom(roomId);
          if (!room) return true;
          
          const event = room.findEventById(r.eventId);
          if (!event) return false; // No event found, likely failed
          
          // Check event status - filter out failed events
          const status = event.status;
          if (status === 'not_sent' || status === 'cancelled') return false;
          
          return true;
        });
        
        if (validReactors.length > 0) {
          filtered.set(emoji, validReactors);
        }
      });
      return filtered;
    }

    // Create a new Map with optimistic changes applied
    const merged = new Map();

    // Copy all server reactions, excluding current user's reactions
    message._reactions.forEach((reactors, emoji) => {
      const filtered = reactors.filter((r) => r.senderId !== currentUserId);
      if (filtered.length > 0) {
        merged.set(emoji, filtered);
      }
    });

    // Add the optimistic reaction
    if (merged.has(optimisticReaction.emoji)) {
      merged.get(optimisticReaction.emoji).push({
        senderId: currentUserId,
        eventId: 'optimistic',
        displayName: 'You',
        isCurrentUser: true,
      });
    } else {
      merged.set(optimisticReaction.emoji, [
        {
          senderId: currentUserId,
          eventId: 'optimistic',
          displayName: 'You',
          isCurrentUser: true,
        },
      ]);
    }

    return merged;
  })();

  const showUnverifiedIcon =
    !isUser &&
    shield &&
    (shield.reason === EventShieldReason.UNVERIFIED_IDENTITY ||
      shield.reason === EventShieldReason.UNSIGNED_DEVICE ||
      shield.reason === EventShieldReason.UNKNOWN_DEVICE ||
      shield.reason === EventShieldReason.VERIFICATION_VIOLATION);

  const isUserClass = isUser ? 'user' : '';
  const positionClass = isFirstInGroup ? 'first' : 'continuation';

  const isRedacted = message.isRedacted();
  const eventStatus = message.status;
  const messageTxnId =
    message.getTxnId?.() ||
    message.getUnsigned?.()?.transaction_id ||
    message.event?.unsigned?.transaction_id ||
    message.txnId ||
    null;
  const isRetryingSend = Boolean(isUser && messageTxnId && retryingTransactionIds?.has(messageTxnId));

  // Prefer queue-derived state (txnId matching) over stale SDK event.status.
  // Priorities (descending): retrying > pending (queue) > unsent (queue) > event.status.
  // When queue says PENDING, we must NOT also set failed from event.status
  // (they are mutually exclusive). This avoids a false error-flash during
  // the brief window where event.status is already 'not_sent' but the queue
  // catch block hasn't yet moved the item to RETRYING.
  let isPendingSend = Boolean(isUser && messageTxnId && pendingTransactionIds?.has(messageTxnId));
  let hasFailedToSend = Boolean(isUser && messageTxnId && unsentTransactionIds?.has(messageTxnId));

  if (!isRetryingSend) {
    if (!isPendingSend) isPendingSend = (eventStatus === 'sending' || eventStatus === 'queued');
    // Only derive failed from event.status when the queue is not actively sending;
    // otherwise a stale 'not_sent' status would override the in-progress clock.
    if (!hasFailedToSend && !isPendingSend) hasFailedToSend = (eventStatus === 'not_sent' || eventStatus === 'cancelled');
  }

  // Get content from either clear content (decrypted) or regular content
  // NOTE: Not using useMemo here for encrypted messages because we need to check
  // the message state on every render to detect when decryption completes
  const getProcessedContent = () => {
    if (isRedacted) {
      const redactionInfo = getRedactionInfo(message, members, client);
      const translation = getRedactionTranslation(redactionInfo, isUser);
      // Disambiguate moderator name when multiple users share the same display name
      if (translation.params.moderatorName && redactionInfo?.redactorId && !redactionInfo.isCurrentUser && needsDisambiguation(redactionInfo.redactorId)) {
        translation.params.moderatorName = `${translation.params.moderatorName} (${redactionInfo.redactorId})`;
      }
      // Only pass params if they're non-empty (for test compatibility)
      return Object.keys(translation.params).length > 0
        ? t(translation.key, translation.params)
        : t(translation.key);
    }

    // For edited messages, use the stored edited content if available
    let content;
    let hasClearContent = false;
    if (message._isEdited && message._editedContent) {
      content = message._editedContent;
      hasClearContent = true; // Edited content means message was successfully decrypted
    } else {
      // Get content from either clear content (decrypted) or regular content
      hasClearContent = message.getClearContent && message.getClearContent();
      content = hasClearContent ? message.getClearContent() : message.getContent();

      // Fallback: check for m.new_content in the current content (for public rooms)
      if (message._isEdited && content['m.new_content']) {
        content = content['m.new_content'];
      }
    }
    
    // A_25514 defensive fallback (see the DECRYPTION_FAILURE_* constants above): if a message
    // from a non-cross-signed sender ever fails to decrypt, show a specific verification-issue
    // reason instead of the generic "Unable to decrypt". Not expected under the current config
    // (inbound decryption is permissive via the vendored patch); fires only if that patch is lost.
    const decryptionFailureReason = message.decryptionFailureReason;
    if (
      decryptionFailureReason === DECRYPTION_FAILURE_UNSIGNED_SENDER ||
      decryptionFailureReason === DECRYPTION_FAILURE_UNKNOWN_SENDER
    ) {
      return `🔒 ${t('ChatView.unableToDecryptUnverifiedSender')}`;
    }

    if (content?.msgtype === MESSAGE_TYPES.BAD_ENCRYPTED) {
      return `🔒 ${t('ChatView.unableToDecrypt')}`;
    }

    // Check if it's an encrypted message that failed to decrypt
    if (message.getType() === MESSAGE_TYPES.ROOM_ENCRYPTED && !hasClearContent) {
      // Check if there's a decryption error
      const decryptionError = message.decryptionError;
      if (decryptionError) {
        // More user-friendly messages for common errors
        if (decryptionError.message?.includes('before this device logged in')) {
          return '🔒 Unable to decrypt - Message was sent before you logged in on this device';
        }
        if (decryptionError.message?.includes('key backup')) {
          return '🔒 Unable to decrypt - Recovery key needed';
        }
        return `🔒 Unable to decrypt: ${decryptionError.message || 'Unknown error'}`;
      }
      logger.debug(`MessageBubble ${message.getId()}: Still waiting for keys`);
      return '🔒 Encrypted message (waiting for keys)';
    }
    return content;
  };

  const processedContent = getProcessedContent();

  // --- Start: Variables now in main component scope ---
  const msgtype = processedContent?.msgtype;
  const isEmote = msgtype === MESSAGE_TYPES.EMOTE;

  const isAttachment = ['m.image', 'm.video', 'm.audio', 'm.file'].includes(msgtype);
  const showFailedAttachmentDelete = isUser && isAttachment && hasFailedToSend;
  const messageContent = message.getClearContent?.() || message.getContent();
  const isBadEncrypted = messageContent?.msgtype === MESSAGE_TYPES.BAD_ENCRYPTED;
  const shouldShowDetails = isAttachment || isLastInGroup || hasRevealedDetails;

  const filename = isAttachment
    ? sanitizeFilename(processedContent.filename || processedContent.body || 'Attachment')
    : null;

  // NEW: Add fileSize and mimeType to main scope
  const fileSize = isAttachment ? processedContent.info?.size : null;
  const mimeType = isAttachment ? processedContent.info?.mimetype : null;
  const formattedSize = formatFileSize(fileSize);
  
  // Get the correct URL for both encrypted and unencrypted attachments
  const attachmentUrl = isAttachment ? getAttachmentUrl(processedContent) : null;

  // --- End: Variables now in main component scope ---
  const thumbnailMimeType = processedContent.info?.thumbnail_info?.mimetype;
  
  // Handle both unencrypted and encrypted thumbnails
  // For encrypted thumbnails: thumbnail_file contains url + encryption keys
  // For unencrypted thumbnails: thumbnail_url is the MXC URL
  const thumbnailFile = processedContent.info?.thumbnail_file;
  const thumbnailUrl = thumbnailFile?.url || processedContent.info?.thumbnail_url;
  
  // Extract stable values for dependency tracking
  const thumbnailKeyJson = thumbnailFile?.key ? JSON.stringify(thumbnailFile.key) : null;
  const thumbnailIv = thumbnailFile?.iv || null;
  const thumbnailHashesJson = thumbnailFile?.hashes ? JSON.stringify(thumbnailFile.hashes) : null;
  const thumbnailVersion = thumbnailFile?.v || null;
  
  // Extract encryption info if thumbnail is encrypted
  // Use useMemo to create a stable reference and prevent infinite re-renders
  const thumbnailEncryptionInfo = useMemo(() => {
    if (!thumbnailKeyJson) return null;
    try {
      return {
        key: JSON.parse(thumbnailKeyJson),
        iv: thumbnailIv,
        hashes: thumbnailHashesJson ? JSON.parse(thumbnailHashesJson) : undefined,
        v: thumbnailVersion,
      };
    } catch (error) {
      logger.error('Failed to parse thumbnail encryption info:', error);
      return null;
    }
  }, [thumbnailKeyJson, thumbnailIv, thumbnailHashesJson, thumbnailVersion]);
  
  // Extract encryption info for full file (similar to thumbnail)
  const fullFile = processedContent.file;
  const fullFileKeyJson = fullFile?.key ? JSON.stringify(fullFile.key) : null;
  const fullFileIv = fullFile?.iv || null;
  const fullFileHashesJson = fullFile?.hashes ? JSON.stringify(fullFile.hashes) : null;
  const fullFileVersion = fullFile?.v || null;
  
  // Extract full file encryption info if encrypted
  const fileEncryptionInfo = useMemo(() => {
    if (!fullFileKeyJson) return null;
    try {
      return {
        key: JSON.parse(fullFileKeyJson),
        iv: fullFileIv,
        hashes: fullFileHashesJson ? JSON.parse(fullFileHashesJson) : undefined,
        v: fullFileVersion,
      };
    } catch (error) {
      logger.error('Failed to parse file encryption info:', error);
      return null;
    }
  }, [fullFileKeyJson, fullFileIv, fullFileHashesJson, fullFileVersion]);
  
  // For images/videos without thumbnails (e.g., from third-party clients),
  // fall back to using the main content URL as the preview
  // NOTE: Only use fallback for unencrypted attachments
  const isEncrypted = !!processedContent.file;
  const shouldUseFallback = !thumbnailUrl && 
    !isEncrypted &&
    (msgtype === 'm.image' || msgtype === 'm.video');
  const fallbackUrl = shouldUseFallback ? processedContent.url : null;
  const fallbackMimeType = shouldUseFallback ? mimeType : null;
  
  const { thumbnailUrl: previewSrc } = useMediaThumbnail(
    message, 
    thumbnailUrl, 
    thumbnailMimeType,
    fallbackUrl,
    fallbackMimeType,
    thumbnailEncryptionInfo, // Pass encryption info for decryption
  );

  // Reset image load error when preview source changes
  useEffect(() => {
    setImageLoadError(false);
  }, [previewSrc]);

  // Get current user ID for self-mention detection
  const currentUserId = client?.getUserId?.();

  // Check if this message mentions the current user (for highlighting)
  const hasSelfMention = currentUserId && (
    processedContent?.['m.mentions']?.user_ids?.includes(currentUserId) ||
    hasMentionForUser(processedContent?.formatted_body, currentUserId)
  );

  /**
   * Render message text with Matrix mention pills
   * Parses formatted_body for Matrix.to links and replaces them with MentionPill components
   */
  const renderMessageWithMentions = useCallback((textBody, formattedBody) => {
    // If no formatted_body or no mentions, return plain text
    if (!formattedBody) {
      return textBody;
    }

    const mentions = parseMentionsFromHtml(formattedBody);
    if (mentions.length === 0) {
      return textBody;
    }

    // Build React elements array with text and pills
    const elements = [];
    let lastIndex = 0;

    mentions.forEach((mention, i) => {
      // Add text before this mention
      if (mention.start > lastIndex) {
        const textBefore = formattedBody.slice(lastIndex, mention.start);
        // Strip HTML tags for plain text display
        const plainTextBefore = textBefore.replace(/<[^>]*>/g, '');
        if (plainTextBefore) {
          linkify(plainTextBefore).forEach((part, j) => {
            elements.push(
              <React.Fragment key={`text-${i}-${j}`}>{part}</React.Fragment>,
            );
          });
        }
      }

      // Add the mention pill
      elements.push(
        <MentionPill
          key={`pill-${i}`}
          userId={mention.userId}
          displayText={mention.displayText}
          onClick={onMentionClick}
        />,
      );

      lastIndex = mention.end;
    });

    // Add remaining text after last mention
    if (lastIndex < formattedBody.length) {
      const remainingHtml = formattedBody.slice(lastIndex);
      // Strip HTML tags for plain text display
      const plainTextRemaining = remainingHtml.replace(/<[^>]*>/g, '');
      if (plainTextRemaining) {
        linkify(plainTextRemaining).forEach((part, j) => {
          elements.push(
            <React.Fragment key={`text-end-${j}`}>{part}</React.Fragment>,
          );
        });
      }
    }

    return elements.length > 0 ? elements : textBody;
  }, [onMentionClick]);

  // This function now just focuses on rendering the body of the message bubble
  const getMessageBody = () => {
    if (typeof processedContent === 'string') {
      // This handles redacted messages and decryption errors
      return processedContent;
    }

    // This function now uses the pre-calculated variables from the main scope
    switch (msgtype) {
      case 'm.text':
      case 'm.notice': {
        let textBody = processedContent.body || '[No content]';
        let formattedBody = processedContent.formatted_body || null;

        // Strip Matrix reply fallback if this is a reply message
        // (messages from other clients include "> <@user:server> text" prefix)
        if (isReply) {
          textBody = stripReplyFallback(textBody);
          // Also strip <mx-reply> from formatted_body
          if (formattedBody) {
            formattedBody = formattedBody.replace(/<mx-reply>[\s\S]*?<\/mx-reply>/gi, '');
          }
        }

        // Render with mention pills if there are mentions
        return renderMessageWithMentions(textBody, formattedBody);
      }

      case MESSAGE_TYPES.EMOTE: {
        // m.emote messages are IRC-style /me actions displayed as "* SenderName action"
        let textBody = processedContent.body || '[No content]';
        let formattedBody = processedContent.formatted_body || null;

        // Strip Matrix reply fallback if this is a reply message
        if (isReply) {
          textBody = stripReplyFallback(textBody);
          if (formattedBody) {
            formattedBody = formattedBody.replace(/<mx-reply>[\s\S]*?<\/mx-reply>/gi, '');
          }
        }

        // Render with mention pills if there are mentions
        const renderedText = renderMessageWithMentions(textBody, formattedBody);
        // Linkify URLs in plain-text emote bodies (when renderMessageWithMentions
        // returns a string, no mentions path). When mentions ARE present, linkify
        // was already applied inside renderMessageWithMentions on text segments.
        const linkifiedEmoteText = typeof renderedText === 'string'
          ? linkify(renderedText)
          : renderedText;

        // Wrap emote content with styling classes for italic action text
        // Display format: "* SenderName action text"
        return (
          <span className="emote-message">
            <span className="emote-prefix">* {name} </span>
            {linkifiedEmoteText}
          </span>
        );
      }

      case 'm.image':
      case 'm.video':
      {
        const onAttachmentClick = (e) => {
          e.preventDefault();
          setIsAttachmentModalOpen(true);
          setIsHovered(false); 
        };
        
        const caption = processedContent.body === filename ? null : processedContent.body;
        const attachmentContent = (
          <>
            <div className='attachment-message'>
              <button
                type='button'
                className={'attachment-preview-btn'}
                onClick={onAttachmentClick}
                aria-label={`Download ${filename || 'file'}`}
              >
                {previewSrc && !imageLoadError ? (
                  <img 
                    src={previewSrc} 
                    alt={`Preview for ${filename}`} 
                    className='attachment-preview' 
                    onError={(e) => {
                      logger.error('Image load error for preview:', {
                        src: previewSrc,
                        error: e,
                        msgtype,
                        mimeType,
                        hasThumbnail: !!processedContent.info?.thumbnail_url,
                      });
                      setImageLoadError(true);
                    }} 
                  />
                ) : (
                  <div className="attachment-preview-fallback">
                    <span>Preview not available</span>
                  </div>
                )}
                {msgtype === 'm.video' && (
                  <div className="video-play-overlay"><i className="fa fa-play"></i></div>
                )}
              </button>
            </div>
            {caption && <div className='attachment-caption'>{caption}</div>}
          </>
        );

        return attachmentContent;
      }

      case 'm.audio':
      case 'm.file': {
        const isTextFile = mimeType && mimeType.includes('text/');
        
        const onAttachmentClick = (e) => {
          e.preventDefault();
          // Open fullscreen modal for text files, regular modal for others
          if (isTextFile) {
            setIsAttachmentModalOpen(true);
          } else {
            setIsFileModalOpen(true);
          }
          setIsHovered(false);
        };

        const fileType = getFileType(mimeType, filename);
        const iconSrc = getAttachmentIcon(msgtype, filename, mimeType);
        const caption = processedContent.body === filename ? null : processedContent.body;

        // Default file icon display
        return (
          <>
            <div
              className='attachment-message attachment-message--file'
              onClick={onAttachmentClick}
            >
              <div className='attachment-file-details'>
                <img src={iconSrc} alt={`Icon for ${filename}`} className={'attachment-icon'} />
                <div className="attachment-file-text-wrapper">
                  <div className='attachment-filename-inline'>{filename}</div>
                  <div className='attachment-file-type-inline'>{fileType}</div>
                  {fileSize > 0 && <div className='attachment-file-size-inline'>{formattedSize}</div>}
                </div>
              </div>
            </div>
            {caption && <div className='attachment-caption'>{caption}</div>}
          </>
        );
      }

      case 'm.location': {
        const geoUri = processedContent.geo_uri;
        const locationBody = processedContent.body;
        const timestamp = message.getTs();
        
        return (
          <LocationMessage 
            geoUri={geoUri} 
            body={locationBody} 
            timestamp={timestamp}
            isUser={isUser}
          />
        );
      }

      default:
        return processedContent.body || '[No content]';
    }
  };

  // Render message content, preserving newlines for text messages
  const messageBody = getMessageBody();
  let renderedContent = messageBody; // messageBody can be JSX or a string
  const isPlainText = typeof renderedContent === 'string';
  const MIN_LINES_FOR_TRUNCATION = 20;
  // Count lines by splitting on newlines and checking array length
  const lineCount = isPlainText ? (renderedContent.match(/\n/g) || []).length + 1 : 0;
  const shouldShowSeeMore = isPlainText && !isRedacted && lineCount >= MIN_LINES_FOR_TRUNCATION;

  // Linkify URLs in text and emote messages only (not attachments or location messages)
  const isLinkifiableType = msgtype === 'm.text' || msgtype === 'm.notice' || isEmote;

  if (isPlainText && renderedContent.includes('\n')) {
    renderedContent = renderedContent.split('\n').map((line, idx, arr) => (
      <React.Fragment key={idx}>
        {isLinkifiableType ? linkify(line) : line}
        {idx < arr.length - 1 && <br />}
      </React.Fragment>
    ));
  } else if (isPlainText && isRedacted) {
    // Wrap redacted text in a clickable span to open message details
    const handleRedactedClick = (e) => {
      e.stopPropagation();
      if (isOpeningDetails) return;
      setIsOpeningDetails(true);
      onMessageDetailsOpen(message);
      setTimeout(() => setIsOpeningDetails(false), 500);
    };
    
    renderedContent = (
      <span 
        className='deleted-message clickable' 
        onClick={handleRedactedClick}
        onMouseDown={(e) => e.preventDefault()}
        role="button"
        tabIndex={0}
        aria-label="View message deletion details"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleRedactedClick(e);
          }
        }}
        title="Click to view message details"
      >
        {renderedContent}
      </span>
    );
  } else if (isPlainText && isLinkifiableType) {
    // Linkify single-line text/emote messages
    renderedContent = linkify(renderedContent);
  }

  // Helper function to render reply preview
  const renderReplyPreview = () => {
    if (!isReply || !originalReplyMessage) return null;

    const { content, sender, senderIsUser, isAttachment, thumbnailUrl, msgtype: replyMsgtype, filename, mimeType, size, eventId, caption } = originalReplyMessage;
    const displaySender = senderIsUser ? 'You' : sender;

    // For attachments, show filename and size (not caption in truncated content)
    let displayContent = content || '';
    if (isAttachment && size) {
      displayContent = `${filename || content || ''} (${size})`;
    } else if (replyMsgtype === MESSAGE_TYPES.EMOTE) {
      // Format emote replies with asterisk prefix: "* SenderName action"
      displayContent = `* ${sender} ${content}`;
    }
    // Truncate displayContent if too long
    const truncatedContent = displayContent.length > 50 ? `${displayContent.substring(0, 50)}...` : displayContent;

    // Get the generic icon for non-image attachments
    const genericIconSrc = isAttachment && !thumbnailUrl
      ? getAttachmentIcon(msgtype, filename, mimeType)
      : null;

    const handleReplyPreviewClick = (e) => {
      e.stopPropagation();
      if (eventId) {
        onFocusMessage(eventId);
      }
    };

    return (
      <div 
        className={`message-reply-preview ${isAttachment ? 'attachment' : ''}`}
        onClick={handleReplyPreviewClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleReplyPreviewClick(e);
          }
        }}
        title="Click to jump to original message"
      >
        {isAttachment && thumbnailUrl && (
          <img src={thumbnailUrl} alt="Reply thumbnail" className="message-reply-preview-thumbnail" />
        )}
        {isAttachment && genericIconSrc && (
          <img src={genericIconSrc} alt="File type icon" className="message-reply-preview-icon" />
        )}
        <div className='message-reply-preview-content'>
          <div className='message-reply-preview-sender'>{displaySender}</div>
          {/* Show caption if it exists, otherwise show file info */}
          {caption ? (
            <div className='message-reply-preview-caption'>
              {caption.length > 60 ? `${caption.substring(0, 60)}...` : caption}
            </div>
          ) : (
            <div className='message-reply-preview-text'>
              {truncatedContent}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderEditIndicator = () => {
    // Don't show edit indicator for redacted messages
    if (!isEdited || isRedacted) return null;

    return (
      <span className='edit-indicator'>
        {' '}({t('MessageBubble.editedLabel')})
      </span>
    );
  };

  useEffect(() => {
    if (isContextMenuOpen) {
      const handleClickOutside = (event) => {
        if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
          setOpenContextMenu(false);
          setIsMenuSticky(false);
        }
      };

      const handleScroll = () => {
        setOpenContextMenu(false);
        setIsMenuSticky(false);
      };

      document.addEventListener('mousedown', handleClickOutside);
      
      // Close menu on scroll - find the messages list container
      const messagesContainer = document.querySelector('.messages-list');
      if (messagesContainer) {
        messagesContainer.addEventListener('scroll', handleScroll);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        if (messagesContainer) {
          messagesContainer.removeEventListener('scroll', handleScroll);
        }
      };
    }
  }, [isContextMenuOpen]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    if (showEmojiPicker) {
      const handleClickOutside = (event) => {
        if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
          setShowEmojiPicker(false);
          setIsMenuSticky(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showEmojiPicker]);

  // Create or get portal container that scrolls with messages
  useEffect(() => {
    if (!portalContainerRef.current) {
      // Find the dedicated portal container in ChatView.jsx
      const portalContainer = document.getElementById('modal-portal-container');
      if (!portalContainer) {
        logger.warn('Could not find #modal-portal-container for modals.');
        return;
      }
      portalContainerRef.current = portalContainer;
    }
  }, []);

  const updateMenuPosition = useCallback((button) => {
    if (!button || !portalContainerRef.current) return;

    const buttonRect = button.getBoundingClientRect();
    const containerRect = portalContainerRef.current.getBoundingClientRect();

    // Calculate item count based on message type and permissions
    let itemCount;
    
    // BAD_ENCRYPTED messages only show Message Details (no reply, edit, delete, or reactions)
    if (isBadEncrypted) {
      itemCount = 1; // Only Message Details
    } else if (isUser) {
      // Check if Edit button will be shown (only for text messages)
      const messageContent = message.getClearContent?.() || message.getContent();
      const isTextMessage = !NON_EDITABLE_MESSAGE_TYPES.includes(messageContent?.msgtype);
      const hasEditHistory = message._isEdited || false;
      
      // User messages: Reply (0-1) + Forward (1) + Pin (0-1) + Edit (0-1) + Details (1) + Edit History (0-1) + Delete (0-1)
      itemCount = 2; // Details (always present) + Forward (always present)
      if (hasMessagePermission) {
        itemCount += 1; // Reply (only if can send messages)
        if (canPin && (isPinned || hasServerEventId)) itemCount++; // Add Pin/Unpin option (only for synced messages or already pinned)
      }
      if (isTextMessage && hasMessagePermission) itemCount++; // Add Edit for text messages (only if can send)
      if (hasEditHistory) itemCount++; // Add Edit History if edited
      if (canDelete && hasMessagePermission) itemCount++; // Add Delete (requires both delete permission AND send permission for own messages)
    } else {
      // Other users' messages: Reply (0-1) + Forward (1) + Pin (0-1) + Details (1) + Edit History (0-1) + Delete (0-1) + Report (1)
      const hasEditHistory = message._isEdited || false;
      itemCount = 3; // Details (always present) + Forward (always present) + Report (always present)
      if (hasMessagePermission) {
        itemCount += 1; // Reply (only if can send messages)
        if (canPin && (isPinned || hasServerEventId)) itemCount++; // Add Pin/Unpin option (only for synced messages or already pinned)
      }
      if (hasEditHistory) itemCount++; // Add Edit History if edited
      if (canDelete) itemCount++; // Add Delete if can delete (moderator)
    }
    
    // Calculate menu height from item count
    const menuHeight = itemCount * CONTEXT_MENU.ITEM_HEIGHT;
    
    // Calculate position relative to the portal container (which scrolls with content)
    const relativeTop = buttonRect.top - containerRect.top;
    const relativeLeft = buttonRect.left - containerRect.left;
    
    // Check if menu would go off the bottom of the messages area
    // .messages-list has height: calc(100% - 77px), so effective bottom is viewport height - 77px
    const messagesAreaBottom = window.innerHeight - 77;
    const spaceBelow = messagesAreaBottom - buttonRect.bottom;
    const shouldExtendUpward = spaceBelow < (menuHeight + 10); // 10px buffer
    
    // Calculate horizontal position (extend left for user, right for others)
    const leftPosition = isUser 
      ? relativeLeft - CONTEXT_MENU.WIDTH 
      : relativeLeft + buttonRect.width;
    
    // Calculate vertical position (extend up if near bottom, otherwise down)
    // When extending upward, account for the hover menu's positioning (top: 0, margin: 3px)
    // This prevents a gap between the menu and the button
    const hoverMenuOffset = 3; // Matches .message-bubble-hover-menu margin-top in CSS
    const topPosition = shouldExtendUpward 
      ? relativeTop - menuHeight + hoverMenuOffset
      : relativeTop + buttonRect.height;
    
    setMenuPosition({
      top: topPosition,
      left: leftPosition,
    });
  }, [isUser, message, canDelete, canPin, isPinned, hasServerEventId, isBadEncrypted, hasMessagePermission]);

  const handleDeleteMessage = (messageId) => {
    showConfirm(
      t('ChatView.deleteMessageTitle'),
      t('ChatView.deleteMessageDesc'),
      () => {
        onDeleteMessage(messageId);
        setOpenContextMenu(false);
      },
      () => {
        setOpenContextMenu(false);
      },
    );
  };

  const handleMessageClick = () => {
    if (!isLastInGroup) {
      setHasRevealedDetails(!hasRevealedDetails);
    }
  };

  const handleMenuClick = (e) => {
    e.stopPropagation();
    
    const button = e.currentTarget;
    updateMenuPosition(button);
    setOpenContextMenu(true);
  };

  // Helper function to calculate emoji picker position based on available space
  const calculateEmojiPickerPosition = useCallback((elementRect, pickerHeight, event, calculateHorizontal = true) => {
    const BELOW_OFFSET = 30;
    const CHATVIEW_TOPBAR_HEIGHT = 56.8;
    // Horizontal offset for emoji picker when there's insufficient space
    // This value (227px) was empirically determined to provide the best visual alignment
    // with the message bubble edge and avoid overflow in constrained spaces
    const EMOJI_PICKER_HORIZONTAL_OFFSET = 227;
    const positionStyle = {};
    
    // Vertical positioning
    const spaceAbove = elementRect.top - CHATVIEW_TOPBAR_HEIGHT;
    const needsSpaceAbove = pickerHeight;

    if (spaceAbove < needsSpaceAbove) {
      // Not enough space above → position below
      positionStyle.top = `${BELOW_OFFSET}px`;
    } else {
      // Enough space above → position above
      positionStyle.top = `-${pickerHeight}px`;
    }
    
    // Horizontal positioning - only calculate when needed (on initial open, not on expand)
    if (calculateHorizontal) {
      let cursorRightSpace, cursorLeftSpace;
      
      if (event && bubbleRef.current) {
        // Find the parent container (messages list or chat view)
        const parentElement = bubbleRef.current.closest('.messages-list') || 
                             bubbleRef.current.closest('.chat-view');
        
        if (parentElement) {
          const parentRect = parentElement.getBoundingClientRect();
          const messageContainerParentWidth = parentElement.clientWidth;
          const messageContainerParentOffsetLeft = parentRect.left;
          
          // Calculate click position and available space
          const clickPositionLeft = event.clientX - messageContainerParentOffsetLeft;
          
          cursorLeftSpace = clickPositionLeft;
          cursorRightSpace = messageContainerParentWidth - clickPositionLeft;
        } else {
          // Fallback to window-based calculation
          cursorRightSpace = window.innerWidth - elementRect.right;
          cursorLeftSpace = elementRect.left;
        }
      } else {
        // Fallback when no event is available (e.g., on expand)
        cursorRightSpace = window.innerWidth - elementRect.right;
        cursorLeftSpace = elementRect.left;
      }
      
      if (isUser && cursorLeftSpace < EMOJI_PICKER_DIMENSIONS.widthAllReactions) {
        // Not enough space on left side
        positionStyle.right = `-${EMOJI_PICKER_HORIZONTAL_OFFSET}px`;
      } else if (cursorRightSpace < EMOJI_PICKER_DIMENSIONS.widthAllReactions) {
        // Not enough space on right side
        positionStyle.left = `-${EMOJI_PICKER_HORIZONTAL_OFFSET}px`;
      }
    }
    
    return positionStyle;
  }, [isUser]);

  const handleReactionClick = useCallback((e) => {
    e.stopPropagation();
    
    const button = e.currentTarget;
    const buttonRect = button.getBoundingClientRect();
    
    // Store the button rect for reuse when picker expands
    setInitialButtonRect(buttonRect);
    
    // Calculate position for compact emoji picker (default reactions only)
    const COMPACT_EMOJI_PICKER_HEIGHT = 55;
    const positionStyle = calculateEmojiPickerPosition(buttonRect, COMPACT_EMOJI_PICKER_HEIGHT, e);
    
    setEmojiPanelPositionStyle((prev) => {
      return { ...prev, ...positionStyle };
    });
    setShowEmojiPicker(!showEmojiPicker);
    setIsMenuSticky(true);
  }, [calculateEmojiPickerPosition, showEmojiPicker]);

  const closeEmojiPicker = useCallback(() => {
    setShowEmojiPicker(false);
    setIsMenuSticky(false);
    setInitialButtonRect(null); // Clear stored rect on close
  }, []);

  const handleEmojiPickerExpand = useCallback(() => {
    // Reuse the stored button rect for consistent positioning reference
    if (!initialButtonRect) return;
    
    // Only recalculate vertical position (height changed), horizontal stays the same
    const positionStyle = calculateEmojiPickerPosition(
      initialButtonRect, 
      EMOJI_PICKER_DIMENSIONS.height + 5, 
      null, 
      false,
    );
    setEmojiPanelPositionStyle((prev) => {
      return { ...prev, ...positionStyle };
    });
  }, [calculateEmojiPickerPosition, initialButtonRect]);

  const renderHoverMenu = (messageId) => {
    if (isServerNotices) {
      return null;
    }

    const shouldShowMenu = isHovered || isHoveringReactions || isContextMenuOpen || isMenuSticky;

    if (!shouldShowMenu) return null;

    // For BAD_ENCRYPTED messages, only show Message Details (no reply, edit, delete, reactions)
    if (isBadEncrypted) {
      const handleMessageDetailsClick = (e) => {
        e.stopPropagation();
        onMessageDetailsOpen(message);
        setOpenContextMenu(false);
        setIsHovered(false);
      };

      return (
        <div
          className={`message-bubble-hover-menu bad-encrypted ${isUserClass}`}
          onMouseEnter={handleOnHover}
          onMouseLeave={() => setIsHovered(false)}>
          <div
            className={`message-bubble-context-menu-wrapper bad-encrypted ${isUser ? 'user' : 'other'}`}>
            <KebabMenuIcon onClick={handleMenuClick} renderSide={isUser ? 'left' : 'right'} />
          </div>
          {isContextMenuOpen && portalContainerRef.current && createPortal(
            <div
              ref={contextMenuRef}
              className='message-bubble-context-menu'
              style={{
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
              }}>
              <div
                className='message-bubble-context-menu-item first last'
                onClick={handleMessageDetailsClick}>
                <i className='fa fa-info-circle' />
                <span className='message-bubble-context-menu-item-text'>{t('MessageBubble.messageDetails', 'Message Details')}</span>
              </div>
            </div>,
            portalContainerRef.current,
          )}
        </div>
      );
    }
    // Determine if this is a text message (only text messages can be edited)
    // Attachments (images, videos, files, audio, location) cannot be edited
    const isTextMessage = !NON_EDITABLE_MESSAGE_TYPES.includes(messageContent?.msgtype);

    // Extract caption for attachments (if body is different from filename)
    const attachmentCaption = isAttachment && processedContent?.body !== filename 
      ? processedContent.body 
      : null;

    const handleReplyClick = (e) => {
      e.stopPropagation();
      onReplyMessage(message, name, messageBody, msgtype, filename, previewSrc, fileSize, attachmentCaption);
      setOpenContextMenu(false);
      setIsHovered(false);
    };

    const handleForwardClick = (e) => {
      e.stopPropagation();

      // Extract message content for forwarding
      // Always use the raw content from processedContent, not the rendered JSX
      let textBody;
      if (isAttachment) {
        // For attachments, use filename as fallback body
        textBody = filename;
      } else {
        // For text messages, use the original body (before JSX rendering)
        textBody = processedContent.body || '';
        // Strip reply fallback if this is a reply
        if (isReply) {
          textBody = stripReplyFallback(textBody);
        }
      }

      const forwardContent = {
        body: textBody,
        msgtype: msgtype,
        filename: filename || null,
        url: processedContent.url || null, // Use actual mxc:// URL, not thumbnail
        info: message.getContent()?.info || null,
        caption: attachmentCaption || null,
        geo_uri: processedContent.geo_uri || null, // For location messages
      };

      startForwarding(message, roomId, forwardContent);
      setOpenContextMenu(false);
      setIsHovered(false);
    };

    const handleEditClick = (e) => {
      e.stopPropagation();

      // Always use the plain text body from processedContent for editing,
      // not messageBody which may contain JSX (e.g., MentionPill components).
      // Mentions are re-processed by enrichEventWithMentions() on send.
      let editContent;
      if (typeof processedContent === 'object' && processedContent.body) {
        editContent = processedContent.body;
        if (isReply) {
          editContent = stripReplyFallback(editContent);
        }
        if (msgtype === MESSAGE_TYPES.EMOTE) {
          editContent = `/me ${editContent}`;
        }
      } else {
        // processedContent is a string (redacted/decryption error) — use directly.
        // Never fall back to messageBody which may contain JSX.
        editContent = typeof processedContent === 'string' ? processedContent : '';
      }

      onEditMessage(message, editContent);
      setOpenContextMenu(false);
      setIsHovered(false);
    };

    const handleDeleteClick = (e) => {
      e.stopPropagation();
      setOpenContextMenu(false);
      setIsHovered(false);
      handleDeleteMessage(messageId);
    };

    const handleMessageDetailsClick = (e) => {
      e.stopPropagation();
      onMessageDetailsOpen(message);
      setOpenContextMenu(false);
      setIsHovered(false);
    };

    const handleEditHistoryClick = (e) => {
      e.stopPropagation();
      onEditHistoryOpen(message);
      setOpenContextMenu(false);
      setIsHovered(false);
    };

    const handleReportClick = (e) => {
      e.stopPropagation();
      setOpenContextMenu(false);
      setIsHovered(false);
      setIsReportModalOpen(true);
    };

    const handlePinClick = async (e) => {
      e.stopPropagation();
      setOpenContextMenu(false);
      setIsHovered(false);

      try {
        if (isPinned) {
          await unpinEvent(message.getId());
        } else {
          await pinEvent(message.getId());
        }
      } catch (error) {
        logger.error('Pin/unpin failed:', error);
        if (error.code === 'PIN_LIMIT_EXCEEDED') {
          showToastError(
            t('PinnedMessages.maxPinsTitle'),
            t('PinnedMessages.maxPinsReached', { count: MAX_PINS_PER_ROOM }),
            { position: 'top-right', countdown: 5 },
          );
        } else {
          showError(
            t('PinnedMessages.actionFailedTitle'),
            isPinned ? t('PinnedMessages.unpinFailed') : t('PinnedMessages.pinFailed'),
          );
        }
      }
    };

    // Helper to render the kebab+context or just kebab
    const renderKebabWithContext = () => (
      <>
        <div className='message-bubble-context-menu-wrapper'>
          <KebabMenuIcon onClick={handleMenuClick} />
        </div>
        {isContextMenuOpen && portalContainerRef.current && createPortal(
          <div
            ref={contextMenuRef}
            className='message-bubble-context-menu'
            style={{
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
            }}>
            {/* For other people's messages: Reply → Forward → Message Details → Edit History (if edited) → Delete (if can delete) */}
            {!isUser && (
              <>
                {hasMessagePermission && (
                  <div className='message-bubble-context-menu-item first' onClick={handleReplyClick}>
                    <i className='fa fa-reply' />
                    <span className='message-bubble-context-menu-item-text'>{t('MessageBubble.reply', 'Reply')}</span>
                  </div>
                )}
                <div className={`message-bubble-context-menu-item ${!hasMessagePermission ? 'first' : ''}`} onClick={handleForwardClick}>
                  <i className='fa fa-share' />
                  <span className='message-bubble-context-menu-item-text'>{t('MessageBubble.forward', 'Forward')}</span>
                </div>
                {/* Show Pin if user has permission AND message has synced to server AND can send messages */}
                {/* Show Unpin if user has permission AND message is already pinned AND can send messages */}
                {canPin && hasMessagePermission && (isPinned || hasServerEventId) && (
                  <div className='message-bubble-context-menu-item' onClick={handlePinClick}>
                    <img src={isPinned ? '/images/svg/unpin.svg' : '/images/svg/pin.svg'} alt={isPinned ? 'unpin' : 'pin'} />
                    <span className='message-bubble-context-menu-item-text'>
                      {isPinned ? t('PinnedMessages.unpinFromRoom') : t('PinnedMessages.pinToRoom')}
                    </span>
                  </div>
                )}
                <div
                  className='message-bubble-context-menu-item'
                  onClick={handleMessageDetailsClick}>
                  <i className='fa fa-info-circle' />
                  <span className='message-bubble-context-menu-item-text'>{t('MessageBubble.messageDetails', 'Message Details')}</span>
                </div>
                {isEdited && (
                  <div
                    className='message-bubble-context-menu-item'
                    onClick={handleEditHistoryClick}>
                    <i className='fa fa-history' />
                    <span className='message-bubble-context-menu-item-text'>{t('MessageBubble.editHistory', 'Edit History')}</span>
                  </div>
                )}
                {canDelete && (
                  <div className='message-bubble-context-menu-item' onClick={handleDeleteClick}>
                    <i className='fa fa-trash' />
                    <span className='message-bubble-context-menu-item-text'>
                      {t('MessageBubble.delete')}
                    </span>
                  </div>
                )}
                <div className='message-bubble-context-menu-item last' onClick={handleReportClick}>
                  <i className='fa fa-flag report-icon' />
                  <span className='message-bubble-context-menu-item-text'>
                    {t('MessageBubble.report', 'Report')}
                  </span>
                </div>
              </>
            )}
            {/* For user's messages: Reply → Forward → Edit (if text) → Message Details → Edit History (if edited) → Delete (if can delete) */}
            {isUser && (
              <>
                {hasMessagePermission && (
                  <div className='message-bubble-context-menu-item first' onClick={handleReplyClick}>
                    <i className='fa fa-reply' />
                    <span className='message-bubble-context-menu-item-text'>{t('MessageBubble.reply', 'Reply')}</span>
                  </div>
                )}
                <div className={`message-bubble-context-menu-item ${!hasMessagePermission ? 'first' : ''}`} onClick={handleForwardClick}>
                  <i className='fa fa-share' />
                  <span className='message-bubble-context-menu-item-text'>{t('MessageBubble.forward', 'Forward')}</span>
                </div>
                {/* Show Pin if user has permission AND message has synced to server AND can send messages */}
                {/* Show Unpin if user has permission AND message is already pinned AND can send messages */}
                {canPin && hasMessagePermission && (isPinned || hasServerEventId) && (
                  <div className='message-bubble-context-menu-item' onClick={handlePinClick}>
                    <img src={isPinned ? '/images/svg/unpin.svg' : '/images/svg/pin.svg'} alt={isPinned ? 'unpin' : 'pin'} />
                    <span className='message-bubble-context-menu-item-text'>
                      {isPinned ? t('PinnedMessages.unpinFromRoom') : t('PinnedMessages.pinToRoom')}
                    </span>
                  </div>
                )}
                {isTextMessage && hasMessagePermission && (
                  <div className='message-bubble-context-menu-item' onClick={handleEditClick}>
                    <i className='fa fa-edit' />
                    <span className='message-bubble-context-menu-item-text'>{t('MessageBubble.edit', 'Edit')}</span>
                  </div>
                )}
                <div className='message-bubble-context-menu-item' onClick={handleMessageDetailsClick}>
                  <i className='fa fa-info-circle' />
                  <span className='message-bubble-context-menu-item-text'>{t('MessageBubble.messageDetails', 'Message Details')}</span>
                </div>
                {isEdited && (
                  <div className='message-bubble-context-menu-item' onClick={handleEditHistoryClick}>
                    <i className='fa fa-history' />
                    <span className='message-bubble-context-menu-item-text'>{t('MessageBubble.editHistory', 'Edit History')}</span>
                  </div>
                )}
                {canDelete && hasMessagePermission && (
                  <div className='message-bubble-context-menu-item last' onClick={handleDeleteClick}>
                    <i className='fa fa-trash' />
                    <span className='message-bubble-context-menu-item-text'>
                      {t('MessageBubble.delete')}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>,
          portalContainerRef.current,
        )}
      </>
    );

    // Helper to render the reaction button (only if user has send permission)
    const renderReactionButton = () => (
      hasMessagePermission && (
        <div className='message-bubble-reaction-button' onClick={handleReactionClick}>
          <div className='reaction-icon-container'>
            <img src='/images/svg/emoji-react.svg' alt='React' className='reaction-icon' />
          </div>
        </div>
      )
    );

    return (
      <div
        className={`message-bubble-hover-menu ${isUserClass} ${!hasMessagePermission ? 'no-reactions' : ''}`}
        onMouseEnter={handleOnHover}
        onMouseLeave={() => setIsHovered(false)} >
        {isUser ? (
          <>
            {renderKebabWithContext()}
            {renderReactionButton()}
          </>
        ) : (
          <>
            {renderReactionButton()}
            {renderKebabWithContext()}
          </>
        )}
        {showEmojiPicker && (
          <div
            ref={emojiPickerRef}
            className='message-bubble-emoji-picker-container'
            style={emojiPanelPositionStyle}
            onClick={(e) => e.stopPropagation()}>
            <SmartEmojiPicker
              messageId={message.getId()}
              reactions={displayReactions}
              onToggleReaction={handleReactionToggle}
              onClose={closeEmojiPicker}
              onExpand={handleEmojiPickerExpand}
            />
          </div>
        )}
      </div>
    );
  };

  const handleReactionToggle = async (messageId, emoji, reactionEventId = null) => {
    try {
      setShowEmojiPicker(false);
      setIsMenuSticky(false);
      setIsHovered(false);

      // If unreacting, clear optimistic state
      if (reactionEventId) {
        setOptimisticReaction(null);
      } else {
        // Set optimistic reaction immediately for instant UI feedback
        setOptimisticReaction({ emoji, timestamp: Date.now() });
      }

      await toggleReaction(messageId, emoji, reactionEventId, message._reactions);
      logger.debug(
        `Toggled reaction ${emoji} on message ${messageId}`,
        reactionEventId ? 'removed' : 'added',
      );

      // Clear optimistic state after a delay to allow server update to arrive
      setTimeout(() => setOptimisticReaction(null), 1000);
    } catch (error) {
      // Clear optimistic state on error
      setOptimisticReaction(null);
      showError(
        t('MessageBubble.reactionFailed'),
        error?.translationKey
          ? t(error.translationKey, {}, error.message)
          : (error?.message || t('MessageBubble.reactionFailedMessage')),
      );
    }
  };

  const handleOnHover = () => {
    setIsHovered(true);
  };

  return (
    <div
      key={message.getId()}
      data-event-id={message.getId()}
      data-author={message.getSender()}
      data-msgtype={message.getContent()?.msgtype || 'm.text'}
      data-timestamp={message.getTs()}
      className={`message-item ${isEditHighlighted ? 'edit-highlighted' : ''} ${isSelected ? 'selected' : ''}`}>
      <div className='message-bubble-wrapper' onClick={handleMessageClick}>
        <div
          className={`message-bubble ${isUserClass} ${positionClass} ${
            isRedacted ? 'redacted' : ''
          } ${displayReactions && displayReactions.size > 0 ? 'has-reactions' : ''} ${
            hasSelfMention ? 'self-mentioned' : ''
          } ${isEmote ? 'emote' : ''}`}
          onMouseEnter={isServerNotices ? undefined : handleOnHover}
          onMouseLeave={isServerNotices ? undefined : () => setIsHovered(false)}
          ref={bubbleRef}>
          {/* Pin indicator for pinned messages */}
          {isPinned && !isRedacted && (
            <div className="message-pin-indicator" title={t('PinnedMessages.pinned')}>
              <img src='/images/svg/pinned.svg' alt='pinned' />
              <span>{t('PinnedMessages.pinned')}</span>
            </div>
          )}
          {renderReplyPreview()}
          <div className={`message-content ${shouldShowSeeMore && !isExpanded ? 'clamped' : ''}`}>
            {renderedContent}
          </div>
          {shouldShowSeeMore && !isExpanded && (
            <button
              className="message-see-more"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(true);
              }}
              type="button"
            >
              <strong>{t('MessageTextContent.seeMore')}</strong>
            </button>
          )}
          {shouldShowSeeMore && isExpanded && (
            <button
              className="message-see-less"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(false);
              }}
              type="button"
            >
              <strong>{t('MessageTextContent.seeLess')}</strong>
            </button>
          )}
          {renderEditIndicator()}

          {/* Display reactions positioned at bottom right of bubble */}
          {!isServerNotices && displayReactions && displayReactions.size > 0 && (
            <MessageReactions
              reactions={displayReactions}
              onToggleReaction={handleReactionToggle}
              messageId={message.getId()}
              onHoverChange={setIsHoveringReactions}
              className={isUser ? 'user' : ''}
              onReactionDetailsOpen={onReactionDetailsOpen}
            />
          )}

          {/* Render the fullscreen attachment modal */}
          {isAttachmentModalOpen && (
            <AttachmentPreviewModal
              url={attachmentUrl} // Use file.url for encrypted, url for unencrypted
              filename={filename}
              msgtype={msgtype}
              fileSize={fileSize}
              mimeType={mimeType}
              message={message}
              senderName={name}
              thumbnailUrl={previewSrc}
              encryptionInfo={fileEncryptionInfo}
              onToggleReaction={handleReactionToggle}
              displayReactions={displayReactions}
              portalContainer={portalContainerRef.current}
              onReplyMessage={onReplyMessage}
              onFocusMessage={onFocusMessage}
              onClose={() => setIsAttachmentModalOpen(false)}
            />
          )}
          {isFileModalOpen && (
            <FileAttachmentModal
              message={message}
              senderName={name}
              thumbnailUrl={previewSrc}
              filename={filename}
              msgtype={msgtype}
              fileSize={fileSize}
              mimeType={mimeType}
              displayReactions={displayReactions}
              onReplyMessage={onReplyMessage}
              portalContainer={portalContainerRef.current}
              onFocusMessage={onFocusMessage}
              onToggleReaction={handleReactionToggle}
              onClose={() => setIsFileModalOpen(false)}
            />
          )}
        </div>
        {!isServerNotices &&!isRedacted && renderHoverMenu(message.getId())}
      </div>
      {filename && !isRedacted && (
        <div className={`attachment-info ${isUser ? 'user' : ''}`}>
          <span className="attachment-info-name">{filename}</span>
          {fileSize > 0 && <span className="attachment-info-size"> ({formattedSize})</span>}
        </div>
      )}


      {shouldShowDetails && (
        <div className='message-info'>
          <FancyTimeStamp
            className='message-timestamp'
            unixTime={message.getTs() / 1000}
            displayTitle={false}
          />
          {!isUser && isLastInGroup && (
            <span className='message-sender'>
              {name}
              {needsDisambiguation(message.getSender()) && (
                <span className='message-sender-mxid'> ({message.getSender()})</span>
              )}
            </span>
          )}
          {isUser && !isBadEncrypted && (
            <>
              <MessageStatusIndicator
                read={isRead}
                failed={hasFailedToSend}
                pending={isPendingSend}
                retrying={isRetryingSend}
                onClick={() => onMessageDetailsOpen(message)}
              />
              {showFailedAttachmentDelete && (
                <button
                  type='button'
                  className='message-failed-attachment-delete'
                  aria-label={t('MessageBubble.delete', 'Delete')}
                  title={t('MessageBubble.delete', 'Delete')}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteMessage(messageId);
                  }}
                >
                  <i className='fa fa-trash' />
                </button>
              )}
            </>
          )}
          {showUnverifiedIcon && <UnverifiedDeviceIcon />}
        </div>
      )}

      {/* Report Message Modal */}
      {isReportModalOpen && (
        <ReportMessageModal
          eventId={messageId}
          isEncrypted={isRoomEncrypted}
          onClose={() => setIsReportModalOpen(false)}
          onSubmit={async (eventId, score, reason) => {
            const result = await reportMessage(eventId, score, reason);
            if (result?.success) {
              showInfo(
                t('ReportMessage.successTitle', 'Report Submitted'),
                t('ReportMessage.successMessage', 'Your report has been submitted successfully.'),
              );
              setIsReportModalOpen(false);
            } else {
              showError(
                t('ReportMessage.errorTitle', 'Report Failed'),
                t('ReportMessage.errorMessage', 'Failed to submit report. Please try again.'),
              );
            }
          }}
        />
      )}
    </div>
  );
};

MessageBubble.propTypes = {
  message: PropTypes.shape({
    isRedacted: PropTypes.func.isRequired,
    getContent: PropTypes.func.isRequired,
    getId: PropTypes.func.isRequired,
    getTs: PropTypes.func.isRequired,
    status: PropTypes.string,
    getSender: PropTypes.func,
    getRoomId: PropTypes.func,
    getType: PropTypes.func,
    getTxnId: PropTypes.func,
    getClearContent: PropTypes.func,
    getUnsigned: PropTypes.func,
    decryptionError: PropTypes.shape({
      message: PropTypes.string,
      code: PropTypes.string,
    }),
    decryptionFailureReason: PropTypes.string,
    client: PropTypes.object,
    _reactions: PropTypes.instanceOf(Map),
    _isEdited: PropTypes.bool,
    _editedContent: PropTypes.object,
  }).isRequired, // It's a MatrixEvent object
  name: PropTypes.string,
  isUser: PropTypes.bool,
  shield: PropTypes.object,
  isRead: PropTypes.bool,
  isFirstInGroup: PropTypes.bool,
  isLastInGroup: PropTypes.bool,
  hasRevealedDetails: PropTypes.bool,
  onDeleteMessage: PropTypes.func,
  onReplyMessage: PropTypes.func,
  onEditMessage: PropTypes.func,
  isEditHighlighted: PropTypes.bool,
  isSelected: PropTypes.bool,
  isReply: PropTypes.bool,
  isEdited: PropTypes.bool,
  originalReplyMessage: PropTypes.shape({
    eventId: PropTypes.string,
    content: PropTypes.string,
    sender: PropTypes.string,
    senderIsUser: PropTypes.bool,
  }),
  onReactionDetailsOpen: PropTypes.func,
  onMessageDetailsOpen: PropTypes.func,
  onFocusMessage: PropTypes.func,
  onEditHistoryOpen: PropTypes.func,
  onMentionClick: PropTypes.func,
  retryingTransactionIds: PropTypes.instanceOf(Set),
  pendingTransactionIds: PropTypes.instanceOf(Set),
  unsentTransactionIds: PropTypes.instanceOf(Set),
};

export default MessageBubble;
